import { useCallback } from 'react';

import { useHistory } from 'react-router-dom';

import { isAuthenticated } from 'actions/user';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import useKYC from 'hooks/useKYC';
import { isUserKycDoneSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';

interface UseSpotTradingAccessOptions {
  source?: 'header' | 'wallet' | 'url' | 'landing_page' | 'markets_list';
  onAccessGranted?: () => void;
  skipTracking?: boolean;
}

interface UseSpotTradingAccessReturn {
  checkAccess: () => boolean;
  handleSpotAccess: () => void;
}

/**
 * Hook to validate and handle spot trading access requirements
 * Checks authentication and KYC status before allowing access
 *
 * @param options - Configuration options
 * @param options.source - Source identifier for analytics tracking
 * @param options.onAccessGranted - Callback executed when access is granted
 * @param options.skipTracking - If true, skips internal mixpanel tracking (caller handles it)
 * @returns Object with checkAccess and handleSpotAccess functions
 */
const useSpotTradingAccess = ({
  source,
  onAccessGranted,
  skipTracking = false,
}: UseSpotTradingAccessOptions): UseSpotTradingAccessReturn => {
  const history = useHistory();
  const isLoggedIn = useAppSelector(state => isAuthenticated(state.user));
  const { isVerified, isKycRefreshRequired, isReduceOnlyEnable } = useKYC();
  const isVerifiedUser = isVerified || isKycRefreshRequired;
  const isTradingEnabled = isVerifiedUser || isReduceOnlyEnable;

  /**
   * Check if user has access to spot trading
   * @returns true if user is authenticated and KYC verified, false otherwise
   */
  const checkAccess = useCallback((): boolean => {
    return isLoggedIn && isTradingEnabled;
  }, [isLoggedIn, isTradingEnabled]);

  const isKycDone = useAppSelector(isUserKycDoneSelector);

  /**
   * Handle spot trading access with proper redirects
   * - If not logged in: redirect to login
   * - If not KYC verified: redirect to KYC verification
   * - If all checks pass: execute onAccessGranted callback
   */
  const handleSpotAccess = useCallback(() => {
    // Track the entry point click (skip if caller handles tracking)
    if (!skipTracking) {
      trackMixpanelEvent(MIXPANEL_EVENT_NAMES.SPOT_TRADING.ENTRYPOINT_CLICKED, {
        source,
        site_demarkation: 'webapp',
        is_kyc_verified: !!isKycDone,
      });
    }

    // Check 1: User authentication
    if (!isLoggedIn) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(`${window.location.pathname}?openSpot=true`);
      history.push(`/login?return_url=${returnUrl}`);
      return;
    }

    // Check 2: KYC verification
    if (!isTradingEnabled) {
      // Redirect to KYC verification
      history.push(routes.account.identityVerification);
      return;
    }
    onAccessGranted?.();
  }, [
    isLoggedIn,
    isTradingEnabled,
    source,
    history,
    onAccessGranted,
    isKycDone,
    skipTracking,
  ]);

  return {
    checkAccess,
    handleSpotAccess,
  };
};

export default useSpotTradingAccess;
